import React, { useState, useEffect } from 'react';
import { Target, Plus, Edit3, Trash2, TrendingUp, Calendar, Trophy, BarChart3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Goals = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ resultadoFinal: 0 });
  const [formData, setFormData] = useState({
    title: '',
    target_amount: '',
    period: 'monthly',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    if (user) {
      loadGoals();
      loadUserStats();
    }
  }, [user]);

  const loadUserStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setStats({ resultadoFinal: data.total_profit || 0 });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadGoals = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/goals?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setGoals(data);
      }
    } catch (error) {
      console.error('Error loading goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Calculate dates based on period if not custom
      let startDate = formData.start_date;
      let endDate = formData.end_date;
      
      if (formData.period !== 'custom') {
        const now = new Date();
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        
        if (formData.period === 'monthly') {
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
        } else if (formData.period === 'quarterly') {
          const quarter = Math.floor(now.getMonth() / 3);
          const endMonth = (quarter + 1) * 3;
          endDate = new Date(now.getFullYear(), endMonth, 0).toISOString().split('T')[0];
        } else if (formData.period === 'yearly') {
          endDate = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
        }
      }
      
      const payload = {
        title: formData.title,
        target_amount: parseFloat(formData.target_amount),
        period: formData.period,
        start_date: startDate,
        end_date: endDate
      };

      const url = editingGoal 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/goals/${editingGoal.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/goals?user_id=${user.id}`;
      
      const method = editingGoal ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await loadGoals();
        setShowModal(false);
        setEditingGoal(null);
        setFormData({
          title: '',
          target_amount: '',
          period: 'monthly',
          start_date: '',
          end_date: ''
        });
        alert('✅ Meta salva com sucesso!');
      } else {
        const errorData = await response.json();
        console.error('Erro ao salvar:', errorData);
        alert(`❌ Erro ao salvar meta: ${errorData.detail || 'Tente novamente.'}`);
      }
    } catch (error) {
      console.error('Error saving goal:', error);
      alert(`❌ Erro ao salvar meta: ${error.message}`);
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title,
      target_amount: goal.target_amount,
      period: goal.period,
      start_date: new Date(goal.start_date).toISOString().split('T')[0],
      end_date: new Date(goal.end_date).toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const handleDelete = async (goal) => {
    if (!confirm(`Tem certeza que deseja excluir a meta "${goal.title}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/goals/${goal.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadGoals();
        alert('✅ Meta excluída com sucesso!');
      } else {
        alert('❌ Erro ao excluir meta. Tente novamente.');
      }
    } catch (error) {
      console.error('Error deleting goal:', error);
      alert('❌ Erro ao excluir meta. Tente novamente.');
    }
  };

  const calculateProgress = (goal) => {
    // Usar o current_amount calculado pelo backend (lucro do período)
    const current = goal.current_amount || 0;
    const progress = (current / goal.target_amount) * 100;
    return Math.min(Math.max(progress, 0), 100);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getDaysRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const setPeriodDates = (period) => {
    const now = new Date();
    let start, end;
    
    switch (period) {
      case 'monthly':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'quarterly':
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        end = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'yearly':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        return;
    }
    
    setFormData(prev => ({
      ...prev,
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    }));
  };

  useEffect(() => {
    if (formData.period !== 'custom') {
      setPeriodDates(formData.period);
    }
  }, [formData.period]);

  // Estatísticas das metas
  const goalStats = {
    total: goals.length,
    active: goals.filter(g => {
      const daysRemaining = getDaysRemaining(g.end_date);
      return daysRemaining >= 0 && g.is_active;
    }).length,
    completed: goals.filter(g => calculateProgress(g) >= 100).length,
    expired: goals.filter(g => {
      const daysRemaining = getDaysRemaining(g.end_date);
      return daysRemaining < 0 && calculateProgress(g) < 100;
    }).length
  };

  return (
    <div className="space-y-6">
      {/* Header Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Target className="w-6 h-6 sm:w-8 sm:h-8 mr-3 text-green-600" />
            Metas de Lucro
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
            Defina e acompanhe seus objetivos financeiros
          </p>
        </div>
        <button
          onClick={() => {
            setEditingGoal(null);
            setFormData({
              title: '',
              target_amount: '',
              period: 'monthly',
              start_date: '',
              end_date: ''
            });
            setShowModal(true);
          }}
          className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          <Plus className="w-5 h-5" />
          <span>Nova Meta</span>
        </button>
      </div>

      {/* Stats Cards Mobile Optimized */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{goalStats.total}</p>
            </div>
            <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Ativas</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">{goalStats.active}</p>
            </div>
            <Target className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Concluídas</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-600">{goalStats.completed}</p>
            </div>
            <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Expiradas</p>
              <p className="text-xl sm:text-2xl font-bold text-red-600">{goalStats.expired}</p>
            </div>
            <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Goals List Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
          Suas Metas
        </h3>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <p className="text-gray-500 ml-3">Carregando metas...</p>
          </div>
        ) : goals.length === 0 ? (
          <div className="text-center py-12">
            <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              Nenhuma meta criada ainda
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              Defina objetivos para acompanhar seu progresso
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="mt-4 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              Criar Primeira Meta
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {goals.map((goal) => {
              const progress = calculateProgress(goal);
              const daysRemaining = getDaysRemaining(goal.end_date);
              const isExpired = daysRemaining < 0;
              const isCompleted = progress >= 100;
              
              return (
                <div key={goal.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 dark:text-white flex items-center text-sm sm:text-base">
                        {isCompleted && <Trophy className="w-4 h-4 text-yellow-500 mr-1 flex-shrink-0" />}
                        <span className="truncate">{goal.title}</span>
                      </h4>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 space-y-1 sm:space-y-0">
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                          {isExpired ? 'Expirada' : `${daysRemaining} dias restantes`}
                        </span>
                        <span className="capitalize">{goal.period}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-2">
                      <button
                        onClick={() => handleEdit(goal)}
                        className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(goal)}
                        className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Progresso</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {progress.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          isCompleted ? 'bg-green-500' :
                          progress > 75 ? 'bg-green-400' :
                          progress > 50 ? 'bg-yellow-400' :
                          progress > 25 ? 'bg-orange-400' : 'bg-red-400'
                        }`}
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      {formatCurrency(goal.current_amount || 0)} / {formatCurrency(goal.target_amount)}
                    </span>
                    {isCompleted && (
                      <span className="text-green-600 dark:text-green-400 font-medium flex items-center">
                        <Trophy className="w-3 h-3 mr-1" />
                        Concluída!
                      </span>
                    )}
                    {isExpired && !isCompleted && (
                      <span className="text-red-600 dark:text-red-400 font-medium">
                        Expirada
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Mobile Optimized */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingGoal ? 'Editar Meta' : 'Nova Meta'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Título da Meta *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: Meta do mês de Janeiro"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor Alvo (R$) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.target_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_amount: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="1000.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Período *
                </label>
                <select
                  value={formData.period}
                  onChange={(e) => setFormData(prev => ({ ...prev, period: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="monthly">📅 Mensal</option>
                  <option value="quarterly">📊 Trimestral</option>
                  <option value="yearly">🎯 Anual</option>
                  <option value="custom">⚙️ Personalizado</option>
                </select>
              </div>

              {formData.period === 'custom' && (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data Início *
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data Fim *
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingGoal(null);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  {editingGoal ? 'Salvar' : 'Criar Meta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Goals;